import { Autocomplete, Container, FormControl, FormHelperText, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import FaltaReceberComponent from 'components/FaltaReceberComponent';
import dayjs from 'dayjs';
import { IAbstractModel } from 'model/abstract.model';
import { ICondicaoPagamento } from 'pages/cadastro-pessoa/pessoa.model';
import { IInfoAdicional, TdadosGeneric, TipoInformacaoAdicional } from 'pages/pagamento/pagamento.model';
import PagamentoService from 'pages/pagamento/pagamento.service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'redux/hooks';
import { deepClone, roundHalfToEven } from 'utils/util';
import { FormaPagamentoDialogContentProps } from '../formapagamento.model';
import LinhasDadosGeneric from '../LinhasDadosGeneric';
import { theme } from 'theme';

const BoletoDialogContent = (props: FormaPagamentoDialogContentProps) => {
    const tipo = TipoInformacaoAdicional.BOLETO;
    const { dadosPagamento, setDadosPagamento } = props.valorRecebido;
    const { faltaReceber } = useAppSelector(s => s.pagamento);
    const { hasError, setHasError } = props.error;
    const dadosPagamentoAsInfoAdicional = dadosPagamento.infoAdicional as IInfoAdicional;
    const { tableRows } = useAppSelector(e => e.pagamento);
    const condicoesPagamento = useAppSelector(e => e.preferencias.condicoesPagamento);
    const [filtroCondicaoPagamento, setFiltroCondicaoPagamento] = useState('');
    const [condicaoPagamentoSelecionada, setCondicaoPagamentoSelecionada] = useState<IAbstractModel | null>(null);
    const service = PagamentoService();


    const filterCondicoesPagamentos = useCallback((arr: ICondicaoPagamento[], filtro: string) => {
        if (!filtro) return arr;
        return arr.filter(e => e.descritivo.toUpperCase().includes(filtro.toUpperCase()));
    }, []);

    useEffect(() => {
        if (props.uuid && condicoesPagamento && condicaoPagamentoSelecionada) {
            const parcelas = service.getParcelasCondicaoPagamentoSelecionada(props.uuid, condicaoPagamentoSelecionada.uuid, (props.customFaltaReceber ?? faltaReceber) - tableRowsValue);
            parcelas.then(response => {
                handleTableRow(response.data.length, response.data);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condicaoPagamentoSelecionada]);

    const tableRowsValue = useMemo(() => {
        return tableRows.filter(e => !e.pago).reduce((_, act) => _ += act.valor, 0);
    }, [tableRows]);

    useEffect(() => {
        if (dadosPagamento.infoAdicional?.tipo && dadosPagamento.infoAdicional?.tipo !== TipoInformacaoAdicional.BOLETO) {
            setDadosPagamento({
                valorRecebido: faltaReceber,
                infoAdicional: undefined
            });
        }

        if (props.isNew && (!condicoesPagamento || condicoesPagamento.length === 0)) {
            setDadosPagamento({
                valorRecebido: faltaReceber,
                infoAdicional: {
                    numeroParcelas: 1,
                    descritivo: '',
                    dadosGeneric: [{
                        valor: faltaReceber - tableRowsValue,
                        vencimento: "",
                        vencimentoDisplay: ""
                    }],
                    tipo
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isNew]);

    const rateiaFaltaReceberEntreParcelas = (numeroParcela: number) => {
        if (dadosPagamento.infoAdicional) {
            const faltaReceberCentavos = faltaReceber * 100;
            const sobraCentavos = faltaReceberCentavos % numeroParcela;
            const valorRateadoCentavos = (faltaReceberCentavos - sobraCentavos) / numeroParcela;
            const ultimaParcelaCentavos = valorRateadoCentavos + sobraCentavos;
            const updateddadosGeneric = dadosPagamento.infoAdicional.dadosGeneric!;
            for (let index = 0; index < numeroParcela; index++) {
                const isUltimaParcela = index === numeroParcela - 1;
                updateddadosGeneric[index] = {
                    valor: (isUltimaParcela ? ultimaParcelaCentavos : valorRateadoCentavos) / 100,
                    vencimento: updateddadosGeneric[index]?.vencimento ?? "",
                    vencimentoDisplay: updateddadosGeneric[index]?.vencimentoDisplay ?? ""
                };
            }

            const updatedInfoAdicional = { ...dadosPagamento.infoAdicional, dadosGeneric: updateddadosGeneric, };

            setDadosPagamento((prevDadosPagamento) => ({
                ...prevDadosPagamento,
                infoAdicional: updatedInfoAdicional,
            }));
        }
    };

    const handleChangeParcela = (value: number) => {
        if (value < 0 || value === 0 || Number.isNaN(value)) return;

        rateiaFaltaReceberEntreParcelas(value);

        setDadosPagamento(prev => {
            return ({ ...prev, infoAdicional: { descritivo: '', numeroParcelas: value, dadosGeneric: prev.infoAdicional?.dadosGeneric, tipo, observacao: prev.infoAdicional?.observacao }, });
        });
    };

    const handleInfoAdicionalObservacao = (observacao: string) => {
        if ((dadosPagamentoAsInfoAdicional)) {
            setDadosPagamento(prevDadosPagamento => ({
                ...prevDadosPagamento,
                infoAdicional: {
                    ...prevDadosPagamento.infoAdicional!,
                    observacao
                },
            }));
        }
    };

    const handleChangeValor = (index: number, valor: number = 0) => {
        if (valor > faltaReceber) {
            setHasError(true);
        } else {
            setHasError(false);
        }
        if ((dadosPagamentoAsInfoAdicional)) {
            const updateddadosGeneric = [...(dadosPagamentoAsInfoAdicional).dadosGeneric!];

            if (index >= 0 && index < updateddadosGeneric.length) {
                updateddadosGeneric[index] = { ...updateddadosGeneric[index], valor };

                setDadosPagamento(prevDadosPagamento => ({
                    ...prevDadosPagamento,
                    infoAdicional: {
                        ...prevDadosPagamento.infoAdicional!,
                        dadosGeneric: updateddadosGeneric,
                    },
                }));
            }
        }
    };

    const somaBoleto = (infos: IInfoAdicional) => {
        let soma = 0;
        infos.dadosGeneric?.forEach((boleto: TdadosGeneric) => {
            soma += boleto.valor;
        });
        return soma;
    };

    const calcularNovaData = (dataAntiga: dayjs.Dayjs | Date) => {
        return dayjs(dataAntiga).add(1, 'month').toDate();
    };

    const handleTableRow = (numeroParcela: number, dadosParcela?: TdadosGeneric[]) => {
        const dadosGeneric = Array.from((dadosPagamentoAsInfoAdicional).dadosGeneric ?? []);

        if (dadosGeneric.length > numeroParcela && numeroParcela > 0) {
            const parcelasExtra = dadosGeneric.length - numeroParcela;
            for (let i = 0; i < parcelasExtra; i++) {
                dadosGeneric.pop();
            }
        }

        if (!condicoesPagamento || condicoesPagamento.length === 0) {
            for (let i = dadosGeneric.length; i < numeroParcela; i++) {
                dadosGeneric.push({
                    valor: faltaReceber,
                    vencimento: "",
                    vencimentoDisplay: ""
                });
            }
        } else if (dadosParcela) {
            for (const parcela of dadosParcela) {
                const date = new Date(parcela.vencimento);
                dadosGeneric.push({
                    valor: parcela.valor,
                    vencimento: date.toISOString(),
                    vencimentoDisplay: date.toISOString()
                });
            }
        }

        setDadosPagamento(prevDadosPagamento => ({
            ...prevDadosPagamento,
            infoAdicional: {
                ...prevDadosPagamento.infoAdicional!,
                dadosGeneric: dadosGeneric,
                observacao: prevDadosPagamento.infoAdicional?.observacao ?? null
            },
        }));
    };

    useEffect(() => {
        if (dadosPagamento.infoAdicional?.dadosGeneric?.length) {
            const total = somaBoleto((dadosPagamentoAsInfoAdicional));
            setDadosPagamento(prev => ({ ...prev, valorRecebido: total }));
            faltaReceber - roundHalfToEven(total, 2) >= 0 ? setHasError(false) : setHasError(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(dadosPagamentoAsInfoAdicional)?.dadosGeneric]);

    useEffect(() => {
        if (dadosPagamento.infoAdicional?.dadosGeneric?.length) {
            handleTableRow((dadosPagamentoAsInfoAdicional)?.numeroParcelas ?? 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(dadosPagamentoAsInfoAdicional)?.numeroParcelas]);


    const handleDatavencimento = (index: number, vencimentoDisplay: string) => {
        if (dadosPagamentoAsInfoAdicional) {
            const cloneDadosGeneric = deepClone((dadosPagamentoAsInfoAdicional).dadosGeneric!);
            if (index === 0) {
                cloneDadosGeneric.forEach(s => {
                    s.vencimentoDisplay = vencimentoDisplay;
                    vencimentoDisplay = calcularNovaData(dayjs(vencimentoDisplay)).toString();
                });
            } else {
                cloneDadosGeneric[index] = { ...cloneDadosGeneric[index], vencimentoDisplay: vencimentoDisplay };
            }

            setDadosPagamento((prevDadosPagamento) => ({
                ...prevDadosPagamento,
                infoAdicional: {
                    ...prevDadosPagamento.infoAdicional!,
                    dadosGeneric: cloneDadosGeneric.map(s => {
                        const display = s.vencimentoDisplay;
                        if (dayjs(display).isValid()) {
                            s.vencimento = new Date(display).toISOString();
                        }
                        return s;
                    }),
                },
            }));
        }
    };

    return (
        <>
            <FormControl
                sx={{ width: condicoesPagamento && condicoesPagamento.length > 0 ? '75%' : '5rem' }}
            >
                {
                    (!condicoesPagamento || condicoesPagamento.length === 0) &&
                    <TextField
                        variant="standard"
                        inputProps={{
                            style: {
                                textAlign: 'center',
                                fontSize: '24px',
                                fontWeight: 700,
                                fontFamily: 'Work Sans, sans-serif'
                            },
                        }}
                        id={'parcelas'}
                        name='parcelas'
                        label="Parcelas"
                        type='number'
                        inputMode='numeric'
                        value={(dadosPagamentoAsInfoAdicional)?.numeroParcelas ?? 1}
                        onChange={e => { handleChangeParcela(parseInt(e.target.value)); }}
                    />
                }
                {condicoesPagamento && condicoesPagamento.length > 0 &&
                    <Autocomplete
                        value={dadosPagamento.infoAdicional?.condicaoPagamento}
                        id="ac-condicao-pagamento-boleto"
                        options={filterCondicoesPagamentos(condicoesPagamento, filtroCondicaoPagamento)}
                        getOptionLabel={(option) => option.descritivo}
                        onInputChange={(_, value) => { setFiltroCondicaoPagamento(value); }}
                        filterOptions={(x) => x}
                        noOptionsText={'Nenhum resultado encontrado'}
                        onChange={(_, condicao) => {
                            setCondicaoPagamentoSelecionada(condicao);
                            setDadosPagamento((prevDadosPagamento) => ({
                                ...prevDadosPagamento,
                                infoAdicional: {
                                    ...prevDadosPagamento.infoAdicional!,
                                    condicaoPagamento: condicao,
                                    numeroParcelas: condicao ? (condicao as ICondicaoPagamento).numeroParcelas : 1,
                                    dadosGeneric: [],
                                    observacao: prevDadosPagamento.infoAdicional?.observacao ?? null,
                                    tipo: tipo
                                },
                            }));
                        }}
                        isOptionEqualToValue={(option, value) => {
                            if (!option || !value) return false;
                            return option.uuid === value.uuid;
                        }}
                        renderInput={
                            (params) => <TextField
                                {...params}
                                label="Condição de pagamento *"
                                variant="standard"
                            />}
                    />}
            </FormControl>

            <Grid container >
                <TableContainer
                    sx={{
                        backgroundColor: theme.palette.background.default,
                        borderRadius: 1,
                        position: 'relative',
                        boxSizing: 'border-box',
                        height: '100%',
                        maxHeight: '320px',
                        minHeight: '320px',
                    }}
                    className="color-orange"
                >
                    <Table
                        id="content-table"
                        stickyHeader
                        sx={{
                            borderRadius: 1,
                        }}
                        size="small"
                    >

                        <TableHead>
                            <TableRow >
                                <TableCell sx={{ width: '3rem' }} align="center">
                                    Valor
                                </TableCell>

                                <TableCell sx={{ width: '3rem' }} align="center">
                                    Vencimento
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {dadosPagamento.infoAdicional && (dadosPagamentoAsInfoAdicional).dadosGeneric?.map((row, index) => (
                                <LinhasDadosGeneric
                                    row={row}
                                    index={index}
                                    handleChangeValor={handleChangeValor}
                                    handleDatavencimento={handleDatavencimento}
                                    hasError={hasError}
                                    key={index}
                                    possuiCondicaoPagamento={!!(condicoesPagamento && condicoesPagamento.length > 0)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Container>
                    {hasError && <FormHelperText sx={{ textAlign: 'center', fontWeight: 600, fontSize: 15, color: theme.palette.error.main }}>O valor não pode ser maior que a venda :(</FormHelperText>}
                    {!hasError && <FormHelperText sx={{ textAlign: 'center', fontWeight: 600, fontSize: 15 }}> </FormHelperText>}
                </Container>
            </Grid>

            <TextField
                label="Observação"
                multiline
                fullWidth
                value={dadosPagamento.infoAdicional?.observacao ?? ""}
                id="boleto-dadospagamento-infoadicional-observacao"
                onChange={(e) => handleInfoAdicionalObservacao(e.target.value)}
                inputProps={{ maxLength: 200 }}
                color='primary'
                focused
            />

            <FaltaReceberComponent
                valorRecebido={dadosPagamento.valorRecebido}
                tableRowsValue={tableRowsValue}
                isAbsolute
            />
        </>
    );
};

export default BoletoDialogContent;
